import { PaginatedQueryResult } from '@dispatch/shared/hooks';
import {
  GetReportsByIdResponseInfo,
  ReportGridInfo,
  ReportsListFilter,
} from '@dispatch/shared/types';
import { PageComponent, TableComponent, TableProps } from '@dispatch/web/ui';
import { Button, Chip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { format } from 'date-fns';
import { startCase } from 'lodash';
import { useMemo } from 'react';

const NO_DATA_MESSAGE = 'N/A';
export interface ReportsListProps {
  reportsQuery: PaginatedQueryResult<GetReportsByIdResponseInfo[], unknown>;
  setFilters: React.Dispatch<React.SetStateAction<ReportsListFilter>>;
  filters: ReportsListFilter;
}

export function ReportsList({
  reportsQuery,
  setFilters,
  filters,
}: ReportsListProps) {
  const data = useMemo(
    () =>
      reportsQuery?.data?.map((report) => ({
        id: report.id,
        reportType: report.reportType,
        fileName: report.fileName,
        createdAt: report.createdAt ?? NO_DATA_MESSAGE,
        filterQuery: report.filterQuery
          ? JSON.stringify(report.filterQuery)
          : NO_DATA_MESSAGE,
        expiresAt: report.report?.expiresAt ?? NO_DATA_MESSAGE,
        status: report.status,
        url: report.report?.url ?? NO_DATA_MESSAGE,
      })),
    [reportsQuery, reportsQuery?.data]
  );

  const ReportStatusChip = (props: { reportStatus?: string }) => {
    switch (props.reportStatus) {
      case 'completed':
        return (
          <Chip
            label={startCase(props.reportStatus)}
            color="success"
            size="medium"
            data-testid="report-status"
          />
        );
      case 'pending':
        return (
          <Chip
            label={startCase(props.reportStatus)}
            color="warning"
            size="medium"
            data-testid="report-status"
          />
        );
      case 'failed':
        return (
          <Chip
            label={startCase(props.reportStatus)}
            color="error"
            size="medium"
            data-testid="report-status"
          />
        );
      default:
        return (
          <Chip
            label={startCase(props.reportStatus)}
            color="info"
            size="medium"
            data-testid="report-status"
          />
        );
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'Report ID', flex: 1 },
    {
      field: 'reportType',
      headerName: 'Report Type',
      flex: 1,
    },
    { field: 'fileName', headerName: 'Report Name', flex: 1.5 },
    {
      field: 'createdAt',
      headerName: 'Created At',
      flex: 1,
      sortable: true,
      valueGetter: ({ value }) => {
        if (value === NO_DATA_MESSAGE) {
          return value;
        }

        return format(new Date(value), 'Pp');
      }, //'Pp' → 06/06/2024, 1:22 PM
    },
    {
      field: 'filterQuery',
      headerName: 'Filter Query',
      flex: 1.5,
      sortable: false,
    },
    {
      field: 'expiresAt',
      headerName: 'Expires At',
      flex: 1,
      valueGetter: ({ value }) => {
        if (value === NO_DATA_MESSAGE) {
          return value;
        }

        return format(new Date(value), 'Pp');
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        const expiresAt = params.row.expiresAt;
        const isExpired = new Date(expiresAt) < new Date();

        return (
          <ReportStatusChip
            reportStatus={isExpired ? 'Expired' : params.value}
          />
        );
      },
    },
    {
      field: 'url',
      headerName: 'Download URL',
      flex: 1,
      renderCell: (params) => {
        const subKey = (
          window as unknown as Window & { env: { API_SERVER_TOKEN: string } }
        ).env.API_SERVER_TOKEN;
        const url = `${params.value}&k=${subKey}`;
        const expiresAt = params.row.expiresAt;
        const isExpired = new Date(expiresAt) < new Date();
        return (
          <Button
            variant="outlined"
            size="small"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!url || url === NO_DATA_MESSAGE || isExpired}
          >
            Download
          </Button>
        );
      },
    },
  ];

  const tableArgs: TableProps<ReportGridInfo> = {
    rows: data || [],
    columns: columns,
    status: reportsQuery.status,
    emptyEntityName: 'Report',
    getRowId: (row) => row.id,
    serverPaging: {
      ...reportsQuery,
      onPageSizeChange: (newPageSize: number) => {
        setFilters({
          ...filters,
          pageSize: newPageSize,
        });
      },
    },
    initialState: {
      sorting: {
        sortModel: [
          {
            field: 'createdAt',
            sort: 'desc',
          },
        ],
      },
    },
  };

  return (
    <PageComponent title="Reports">
      <div style={{ width: '100%' }}>
        <TableComponent {...tableArgs} />;
      </div>
    </PageComponent>
  );
}
